import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";

import { FavoriteHeartIcon } from "@/components/FavoriteHeartIcon";
import { useFirebaseAuth } from "@/components/auth/FirebaseAuthProvider";
import {
  turkuIconActionButtonActiveClass,
  turkuIconActionButtonClass,
} from "@/components/turku/turkuIconActionButton";
import {
  addFavoriteSongId,
  removeFavoriteSongId,
} from "@/lib/firebase/favorites";
import {
  peekCachedFavoriteIds,
  refreshFavoriteSongIds,
} from "@/lib/firebase/favorite-ids";
import { waitForAuthToken } from "@/lib/firebase/wait-for-auth";

export type TurkuFavoriteLabels = {
  signIn: string;
  signInAction: string;
  dismiss: string;
  addToFavorites: string;
  removeFromFavorites: string;
  favoriteError: string;
};

type TurkuDetailFavoriteProps = {
  songId: string;
  labels: TurkuFavoriteLabels;
  accountHref: string;
  returnPath: string;
};

function buildSignInHref(accountHref: string, returnPath: string): string {
  const base = accountHref.startsWith("http")
    ? new URL(accountHref)
    : new URL(accountHref, "https://sazsoz.app");
  if (returnPath.startsWith("/")) {
    base.searchParams.set("return", returnPath);
  }
  return `${base.pathname}${base.search}`;
}

export function TurkuDetailFavorite({
  songId,
  labels,
  accountHref,
  returnPath,
}: TurkuDetailFavoriteProps) {
  const normalizedSongId = songId.trim();
  const signInHref = buildSignInHref(accountHref, returnPath);
  const { user, authReady } = useFirebaseAuth();
  const [isFavorite, setIsFavorite] = useState(false);
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSignInHint, setShowSignInHint] = useState(false);

  const syncFavoriteState = useCallback(
    async (uid: string) => {
      const cached = peekCachedFavoriteIds(uid);
      if (cached) {
        setIsFavorite(cached.includes(normalizedSongId));
      }
      try {
        await waitForAuthToken();
        const ids = await refreshFavoriteSongIds(uid);
        setIsFavorite(ids.includes(normalizedSongId));
      } catch {
        /* keep cache-derived state */
      }
    },
    [normalizedSongId],
  );

  useEffect(() => {
    if (!authReady) {
      return;
    }
    if (!user?.uid) {
      setIsFavorite(false);
      return;
    }
    let cancelled = false;
    void syncFavoriteState(user.uid).then(() => {
      if (cancelled) {
        return;
      }
    });
    return () => {
      cancelled = true;
    };
  }, [authReady, user?.uid, syncFavoriteState]);

  const handleClick = useCallback(async () => {
    if (!normalizedSongId) {
      return;
    }

    setError(null);
    const wasFavorite = isFavorite;

    if (!user?.uid) {
      setShowSignInHint(true);
      return;
    }

    const uid = user.uid;
    const nextFavorite = !wasFavorite;

    setMutating(true);
    setIsFavorite(nextFavorite);
    setShowSignInHint(false);

    try {
      await waitForAuthToken();

      if (nextFavorite) {
        await addFavoriteSongId(uid, normalizedSongId);
      } else {
        await removeFavoriteSongId(uid, normalizedSongId);
      }

      const ids = await refreshFavoriteSongIds(uid);
      setIsFavorite(ids.includes(normalizedSongId));
    } catch {
      setIsFavorite(wasFavorite);
      void syncFavoriteState(uid);
      setError(labels.favoriteError);
    } finally {
      setMutating(false);
    }
  }, [
    isFavorite,
    normalizedSongId,
    labels.favoriteError,
    user?.uid,
    syncFavoriteState,
  ]);

  const ariaLabel = isFavorite
    ? labels.removeFromFavorites
    : labels.addToFavorites;
  const hint = !user && authReady ? labels.signIn : ariaLabel;

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        className={clsx(
          turkuIconActionButtonClass,
          isFavorite && turkuIconActionButtonActiveClass,
          mutating && "cursor-wait opacity-75",
          !authReady && "opacity-40",
        )}
        onClick={() => void handleClick()}
        disabled={mutating || !normalizedSongId || !authReady}
        aria-pressed={isFavorite}
        aria-busy={mutating}
        aria-label={ariaLabel}
        aria-describedby={showSignInHint ? "turku-fav-signin-hint" : undefined}
        title={hint}
      >
        <FavoriteHeartIcon filled={isFavorite} />
        {error ? <span className="sr-only">{error}</span> : null}
      </button>

      {showSignInHint ? (
        <div
          id="turku-fav-signin-hint"
          role="status"
          className="absolute right-0 top-full z-30 mt-2 w-72 rounded-2xl border border-white/15 bg-[#252525] p-4 text-sm shadow-[0_12px_40px_rgba(0,0,0,0.55)]"
        >
          <p className="leading-relaxed text-white/85">{labels.signIn}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <a
              href={signInHref}
              className="inline-flex rounded-full bg-accent-primary px-3.5 py-1.5 text-xs font-semibold text-black transition hover:shadow-[0_0_20px_rgba(252,191,34,0.25)]"
            >
              {labels.signInAction}
            </a>
            <button
              type="button"
              onClick={() => setShowSignInHint(false)}
              className="text-xs font-medium text-white/55 transition hover:text-white/85"
            >
              {labels.dismiss}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
